import React, { useState } from "react";
import Button from "../../components/Button";
import SmartForm from "../../components/SmartForm";
import { getCounterBlockedStatus, putAcceptOrderRequest, putRejectOrderRequest } from "src/services/order-api";
import { RootState } from "src/context/store";
import { useSelector } from "react-redux";
import { useToast } from "src/hooks/useToast";
import { useEventStatus } from "src/hooks/useEventStatus";
import FormSuccess from "../../container/FormSuccess";

interface OrderApprovalFormProps {
  type: boolean;
  order_id: number;
  closeModal: (modalName: string) => void;
  onSuccess: (streamId: string) => void;
  position_code: string;
}

const OrderApprovalForm: React.FC<OrderApprovalFormProps> = ({ type, order_id, closeModal, onSuccess, position_code }) => {
  const { orderDetailsFiltered } = useSelector((state: RootState) => state.ord);
  const [remarks, setRemarks] = useState("");
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const orderDetails = orderDetailsFiltered?.[0];
  const [isCounterBlockedModalOpen, setIsCounterBlockedModalOpen] = useState(false);
  const [approvalData, setApprovalData] = useState<any>(null);

  const { withEventCheck } = useEventStatus();

  const handleSubmitRequest = async (data: any) => {
    const counterBlockedStatus = await getCounterBlockedStatus(orderDetails?.counter_ff_div_lnk_id);
    if (
      counterBlockedStatus?.data?.data?.counter_order_block?.length > 0 ||
      counterBlockedStatus?.data?.data?.counter_company_lnk_id?.counter_order_block?.length > 0
    ) {
      setIsCounterBlockedModalOpen(true);
      setIsLoading(false);
      const nongst_orderlimit_value = counterBlockedStatus?.data?.data?.counter_company_lnk_id?.nongst_orderlimit_value ?? null;
      const gstAvailable = counterBlockedStatus?.data?.data?.counter_company_lnk_id?.counter_location?.counter_hdr_id?.gst_available === "Yes";
      if (!gstAvailable && Number(nongst_orderlimit_value) && Number(orderDetailsFiltered?.[0]?.billed_price) > Number(nongst_orderlimit_value)) {
        toast({ description: `Non-GST counter and order value has breached the ₹${nongst_orderlimit_value} limit`, variant: "destructive" });
      }
      return;
    } else {
      type ? acceptRequestHandler(data) : rejectRequestHandler(data);
      const nongst_orderlimit_value = counterBlockedStatus?.data?.data?.counter_company_lnk_id?.nongst_orderlimit_value ?? null;
      const gstAvailable = counterBlockedStatus?.data?.data?.counter_company_lnk_id?.counter_location?.counter_hdr_id?.gst_available === "Yes";
      if (!gstAvailable && Number(nongst_orderlimit_value) && Number(orderDetailsFiltered?.[0]?.billed_price) > Number(nongst_orderlimit_value)) {
        toast({ description: `Non-GST counter and order value has breached the ₹${nongst_orderlimit_value} limit`, variant: "destructive" });
      }
    }
  }

  async function acceptRequestHandler(data: any) {
    if (isLoading) return;
    setIsLoading(true);
    if (!data?.remarks?.trim()) {
      toast({
        description: "Comment cannot be empty",
        variant: "destructive",
      });
      return;
    }
    await withEventCheck(
      () => {

      },
      `order_${orderDetails?.order_id_int}`
    );
    try {
      const payload = {
        data: {
          position_code: position_code,
          order_id: order_id,
          action: "approve",
          remarks: data?.remarks.trim(),
        },
        event_type: "ORDER_APPROVE",
        primary_key: order_id,
      };
      // console.log({ payload });
      const res = await putAcceptOrderRequest(payload, orderDetails?.company_code);

      if (res.status !== 200) throw new Error();
      setTimeout(() => {
        onSuccess(res?.data?.data?.data?.eventStreamCreated?.streamId);
        closeModal("approveCounter");
        setIsLoading(false);
      }, 1000);
      toast({
        description: "Order has been moved to next level for approval",
        variant: "default",
      });
    } catch (error: any) {
      console.log(error);
      closeModal("approveCounter");
      setIsLoading(false);
      toast({
        description: error?.response?.data?.message || "Failed to approve order",
        variant: "destructive",
      });
    }
  }

  async function rejectRequestHandler(data: any) {
    if (isLoading) return;
    setIsLoading(true);
    await withEventCheck(
      () => {

      },
      `order_${orderDetails?.order_id_int}`
    );
    if (!data?.remarks?.trim()) {
      toast({
        description: "Comment cannot be empty",
        variant: "destructive",
      });
      return;
    }
    try {
      const payload = {
        data: {
          position_code: position_code,
          order_id: order_id,
          remarks: data?.remarks?.trim(),
        },
        event_type: "ORDER_REJECT",
        primary_key: order_id,
      };

      const res = await putRejectOrderRequest(payload, orderDetails?.company_code);

      if (res.status !== 200) throw new Error();
      closeModal("rejectCounter");
      setTimeout(() => {
        onSuccess(res?.data?.data?.data?.eventStreamCreated?.streamId);
        closeModal("rejectCounter");
        setIsLoading(false);
      }, 1000);
      toast({
        description: "Order has been rejected successfully",
        variant: "default",
      });
    } catch (error: any) {
      closeModal("rejectCounter");
      setIsLoading(false);
      toast({
        description: error?.response?.data?.message || "Failed to reject order",
        variant: "destructive",
      });
    }
  }

  return (
    <SmartForm
      isControlled={false}
      defaultValues={{}}
      onFormSubmit={(data: any) => {
        // type ? acceptRequestHandler(data) : rejectRequestHandler(data);
        setApprovalData(data)
        handleSubmitRequest(data)
      }}
    >
      <section className="flex flex-col gap-2 px-5 py-5 w-100">
        <SmartForm.TextArea
          type="text"
          value={remarks}
          onChange={(e: any) => {
            setRemarks(e.target.value);
          }}
          className=""
          placeholder="Type comment here..."
          fieldName="remarks"
          label="Comments"
        />
      </section>
      <footer className="border border-t-[1px] flex justify-end px-5 py-3 gap-3 rounded-bl-2xl rounded-br-2xl">
        <Button className="border" onClick={() => (type ? closeModal("approveCounter") : closeModal("rejectCounter"))}>
          Cancel
        </Button>
        <Button isLoading={isLoading} loaderSize="small" type="submit" className="text-white bg-[#586AF5]">
          Submit
        </Button>
      </footer>
      <FormSuccess
        showForm={isCounterBlockedModalOpen}
        onContinue={() => {
          setIsCounterBlockedModalOpen(false)
          type ? acceptRequestHandler(approvalData) : rejectRequestHandler(approvalData);
        }}
        variant="error"
        popupMessage={{
          description: "This counter is currently blocked. Do you still want to proceed with approving the order?",
          title: "Counter Blocked!",
        }}
      />
    </SmartForm>
  );
};

export default OrderApprovalForm;
